import React, { useState } from 'react';
import { Drawer } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import {
  PersonSearch,
  BookmarksOutlined,
  ChevronLeft,
  ChevronRight,
  AutoStories,
  AssignmentInd,
  Approval,
  Groups3,
  Assessment,
  Menu,
  AssignmentTurnedInOutlined,
  CheckCircleOutline,
} from '@mui/icons-material';
import { PAGE_COMMON_TEXT } from '../lang';
import { MONITOR_SIDEBAR_MODULE } from '../constants';
import { SUPERVISOR_MONITOR_LIST_MODULE } from '../../../Nomination/components/constant';
import { useAuth } from '../../../../contexts';
import {
  type SidebarData,
  Sidebar,
  IconButton,
  Button,
  handleRoleNavigation,
} from '../../../../components';

const SIDEBAR_TEXT = PAGE_COMMON_TEXT.SIDEBAR_TEXT;
const drawerWidth = 260;

export type MonitorSidebarProps = {
  onChangeActivated: (activatedId: MONITOR_SIDEBAR_MODULE) => void;
  generalMode?: boolean; // default to be used as general monitor
  activatedModule?: MONITOR_SIDEBAR_MODULE;
};

export const MonitorSideBar: React.FunctionComponent<MonitorSidebarProps> = (
  props: MonitorSidebarProps,
) => {
  const { onChangeActivated, activatedModule, generalMode = true } = props;
  const [open, setOpen] = useState(true);
  const [defaultActivatedModule, setDefaultActivatedModule] = useState(
    activatedModule ?? MONITOR_SIDEBAR_MODULE.STUDENT_APPLICATION,
  );

  const sidebarData: SidebarData[] = [
    {
      id: MONITOR_SIDEBAR_MODULE.STUDENT_APPLICATION,
      name: SIDEBAR_TEXT.STUDENT_APPLICATION,
      icon: <PersonSearch />,
    },
    {
      id: MONITOR_SIDEBAR_MODULE.SUPERVISOR_NOMINATION,
      name: SIDEBAR_TEXT.SUPERVISOR_NOMINATION,
      icon: <Groups3 />,
    },
    ...(generalMode
      ? [
          {
            id: MONITOR_SIDEBAR_MODULE.DORPGS_ASSESSMENT,
            name: SIDEBAR_TEXT.DORPGS_ASSESSMENT,
            icon: <BookmarksOutlined />,
          },
          {
            id: MONITOR_SIDEBAR_MODULE.SCHOLARSHIP_EVALUATION,
            name: SIDEBAR_TEXT.SCHOLARSHIP_EVALUATION.TITLE,
            icon: <Assessment />,
          },
          {
            id: MONITOR_SIDEBAR_MODULE.APPROVAL,
            name: SIDEBAR_TEXT.APPROVAL,
            icon: <Approval />,
            children: [
              {
                id: MONITOR_SIDEBAR_MODULE.DRC_APPROVAL,
                name: SIDEBAR_TEXT.DRC_APPROVAL,
              },
              {
                id: MONITOR_SIDEBAR_MODULE.HOD_DOS_ASSESSMENT,
                name: SIDEBAR_TEXT.HOD_DOS_ASSESSMENT,
              },
              {
                id: MONITOR_SIDEBAR_MODULE.FRC_ASSESSMENT,
                name: SIDEBAR_TEXT.FRC_ASSESSMENT,
              },
            ],
          },
          {
            id: MONITOR_SIDEBAR_MODULE.GS_DECISION,
            name: SIDEBAR_TEXT.GS_DECISION,
            icon: <AssignmentTurnedInOutlined />,
          },
          {
            id: MONITOR_SIDEBAR_MODULE.CONDITION_CHECK,
            name: SIDEBAR_TEXT.CONDITION_CHECK,
            icon: <CheckCircleOutline />,
          },
          {
            id: MONITOR_SIDEBAR_MODULE.QUALIFICATION_CHECK,
            name: SIDEBAR_TEXT.QUALIFICATION_CHECK,
            icon: <AutoStories />,
          },
          {
            id: MONITOR_SIDEBAR_MODULE.VISA_APPLICATION,
            name: SIDEBAR_TEXT.VISA_APPLICATION,
            icon: <AssignmentInd />,
          },
        ]
      : [
          {
            id: MONITOR_SIDEBAR_MODULE.DECLARATION,
            name: SIDEBAR_TEXT.DECLARATION,
            icon: <BookmarksOutlined />,
          },
        ]),
  ];

  const drawerContentStyle: React.CSSProperties = {
    transition: 'padding-top 0.25s ease',
    overflowY: 'auto',
    overflowX: 'hidden',
    scrollBehavior: 'smooth',
    scrollbarWidth: 'thin',
    position: 'sticky',
  };

  const theme = useTheme();

  const handleDrawerOpen = (): void => {
    setOpen(true);
  };

  const handleDrawerClose = (): void => {
    setOpen(false);
  };

  const handleChangeActivated = (activatedId: string): void => {
    onChangeActivated(activatedId as MONITOR_SIDEBAR_MODULE);
    setDefaultActivatedModule(activatedId as MONITOR_SIDEBAR_MODULE);
  };

  const { user, role: currentRole } = useAuth();

  const guestMode = user?.userInfo.netId === 'guest';

  // back to the home page of current role
  const handleBack = (am?: string): void => {
    if (am !== undefined) {
      window.location.href = `/internal/nominations?am=${am}`; // in case co-supervisor want to go back exactly to Co-supervisor Declaration page
    } else if (currentRole !== null) {
      handleRoleNavigation(currentRole);
    }
  };

  const DrawerHeader = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0, 3),
    ...theme.mixins.toolbar,
    justifyContent: 'flex-end',
  }));

  return (
    <>
      <div
        style={{
          position: 'relative',
          top: '1rem',
          left: '1.25rem',
          width: '2.25rem',
        }}
      >
        <IconButton
          color="default"
          aria-label="open drawer"
          onClick={handleDrawerOpen}
          edge="start"
          sx={{ mr: 2, ...(open && { display: 'none' }), position: 'fixed' }}
          icon={<Menu />}
        />
      </div>
      <Drawer
        style={{
          top: '5rem',
          position: 'sticky',
          minHeight: '100vh',
          height: '100%',
        }}
        sx={{
          width: `${open ? drawerWidth : 0}px`,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: `${open ? drawerWidth : 0}px`,
            boxSizing: 'border-box',
          },
          '& .sc-jTrNMu': {
            width: '14rem',
          },
          '& .MuiPaper-root': {
            position: 'sticky',
            height: 'calc(100vh - 5rem)',
            top: '5rem',
          },
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
          flexGrow: 1,
          ...(!open && { display: 'none' }),
        }}
        variant="persistent"
        anchor="left"
        open={open}
      >
        <div style={drawerContentStyle}>
          <DrawerHeader>
            <IconButton
              onClick={handleDrawerClose}
              style={{ marginBottom: '-1.25rem' }}
              icon={
                theme.direction === 'ltr' ? <ChevronLeft /> : <ChevronRight />
              }
            />
          </DrawerHeader>
          <Sidebar
            defaultActivatedId={defaultActivatedModule}
            sidebarData={sidebarData}
            onChangeActivated={handleChangeActivated}
            barInnerWidth="250px"
          />
          <div className="flex flex-row justify-center align-center w-100 my-4">
            {generalMode && (
              <Button
                className="m-[2rem]"
                variant="outlined"
                onClick={() => {
                  handleBack();
                }}
              >
                {PAGE_COMMON_TEXT.BACK}
              </Button>
            )}
            {!(generalMode || guestMode) && (
              <Button
                className="m-[2rem]"
                variant="outlined"
                onClick={() => {
                  handleBack(
                    SUPERVISOR_MONITOR_LIST_MODULE.CO_SUPERVISOR_DECLARATION,
                  );
                }}
              >
                {PAGE_COMMON_TEXT.BACK}
              </Button>
            )}
          </div>
        </div>
      </Drawer>
    </>
  );
};
